/**
 * Reading the authorization response the IdP leaves on the portal root.
 *
 * Deliberately free of imports. `src/main.tsx` pulls this in at module scope so the
 * response can be taken off the URL before anything else runs, and the file it is read
 * from must not drag token storage or configuration along with it.
 */

/**
 * Every parameter the IdP may add to the redirect. Removed by name rather than by
 * replacing the query string, because a launch URL can also carry `idp` and
 * `login_hint`, and `captureLaunchParameters()` reads those slightly later in startup.
 */
const RESPONSE_PARAMS = [
  'code',
  'state',
  'scope',
  'session_state',
  'iss',
  'error',
  'error_description',
  'error_uri'
];

export interface AuthorizationResponse {
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
  /** RFC 9207 issuer identifier, when the IdP sends one. */
  iss?: string;
}

/** A response that cannot be turned into tokens. Always terminal. */
export class AuthorizationResponseError extends Error {
  readonly oidcError: string;
  readonly terminal = true;

  constructor(oidcError: string, description?: string) {
    super(description ? `${oidcError}: ${description}` : oidcError);
    this.name = 'AuthorizationResponseError';
    this.oidcError = oidcError;
  }
}

/**
 * Reads the authorization response off the current URL and removes it, synchronously.
 *
 * Must run before anything can navigate. Scrubbing later, from inside React, would
 * change `useLocation()` while the token request is still in flight and re-run the
 * effects that decide whether we are authenticated — which concluded we are not, and
 * redirected to `/login` mid-exchange.
 *
 * `replaceState` rather than `pushState`, so the URL carrying the code never enters
 * session history and Back cannot return to it.
 */
export function captureAuthorizationResponse(): AuthorizationResponse | undefined {
  let search: string;
  try {
    search = window.location.search ?? '';
  } catch {
    return undefined;
  }

  if (!search) return undefined;

  const params = new URLSearchParams(search);
  const code = params.get('code') ?? undefined;
  const error = params.get('error') ?? undefined;

  if (!code && !error) return undefined;

  const response: AuthorizationResponse = {
    code,
    error,
    state: params.get('state') ?? undefined,
    errorDescription: params.get('error_description') ?? undefined,
    iss: params.get('iss') ?? undefined
  };

  RESPONSE_PARAMS.forEach((key) => params.delete(key));

  try {
    const remaining = params.toString();
    const { pathname, hash } = window.location;
    window.history.replaceState(null, '', `${pathname}${remaining ? `?${remaining}` : ''}${hash}`);
  } catch (scrubError) {
    console.warn('[OIDC] Failed to scrub the authorization response from the URL:', scrubError);
  }

  return response;
}
